// Uploading a stored file drop (docs/adr/0015): the files are read a record
// at a time, sealed in this tab by @sss/crypto, and sent in parts of whole
// records. Parts go three at a time and are retried until they arrive or
// the sender stops; a part that already arrived is never sent again.

import { DROP_RECORD_BYTES, DROP_RECORD_PLAINTEXT_BYTES, type DropSession } from '@sss/crypto';
import { DROP_KEEP_RATE, DROP_MIN_CHARGE_SECONDS } from '@sss/types';
import { isRetryable } from './dropsApi';

/** Where a run of the joined data comes from: a slice of one file. */
export interface Span {
  file: number;
  offset: number;
  length: number;
}

/** The slices of `sizes`' files that make up bytes [start, start + length) of their joined data. */
export const spans = (sizes: number[], start: number, length: number): Span[] => {
  const out: Span[] = [];
  let fileStart = 0;
  let want = start;
  let left = length;
  for (const [file, size] of sizes.entries()) {
    if (left === 0) break;
    const fileEnd = fileStart + size;
    if (want < fileEnd && size > 0) {
      const offset = want - fileStart;
      const take = Math.min(size - offset, left);
      out.push({ file, offset, length: take });
      want += take;
      left -= take;
    }
    fileStart = fileEnd;
  }
  return out;
};

/** Read bytes [start, start + length) of the files joined in order. */
export const readJoined = async (files: File[], start: number, length: number): Promise<Uint8Array<ArrayBuffer>> => {
  const parts = spans(
    files.map((f) => f.size),
    start,
    length,
  ).map((s) => (files[s.file] as File).slice(s.offset, s.offset + s.length));
  return new Uint8Array(await new Blob(parts).arrayBuffer());
};

/** What the server will hold from the pass, worked out the same way (apps/worker src/dropStore.ts holdFor): never less than the minimum charge. */
export const dropHold = (totalRecords: number, headerBytes: number, sealedManifestBytes: number, keepSeconds: number): number => {
  const stored = totalRecords * DROP_RECORD_BYTES + headerBytes + sealedManifestBytes;
  return Math.ceil((stored * DROP_KEEP_RATE.bytes * Math.max(keepSeconds, DROP_MIN_CHARGE_SECONDS)) / DROP_KEEP_RATE.seconds);
};

/** Records [first, first + count) of the part numbered `part` (1-based). */
export const partRecords = (part: number, partSize: number, totalRecords: number): { first: number; count: number } => {
  const first = (part - 1) * partSize;
  return { first, count: Math.max(0, Math.min(partSize, totalRecords - first)) };
};

/** Seconds to wait before retry number `attempt` (0-based): 1, 2, 4, 8, 16, then 30 each time. */
export const retryDelay = (attempt: number): number => (attempt < 5 ? 2 ** attempt : 30);

const wait = (seconds: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, seconds * 1000);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(signal.reason);
    });
  });

export interface UploadOptions {
  files: File[];
  session: DropSession;
  parts: number;
  partSize: number;
  /** Parts already uploaded (a resumed upload): skipped. */
  done: Set<number>;
  put(part: number, body: Uint8Array<ArrayBuffer>, signal: AbortSignal, onProgress: (sent: number) => void): Promise<unknown>;
  onPart(part: number): void;
  /** Bytes sent so far, every part together (a retried part starts its count again). */
  onBytes?(sent: number): void;
  /** Called while a part is waiting to retry; `null` once it carries on. */
  onRetry?(seconds: number | null): void;
  signal: AbortSignal;
  concurrency?: number;
}

/** Seal one part's records into its body. */
export const sealPart = async (files: File[], session: DropSession, first: number, count: number): Promise<Uint8Array<ArrayBuffer>> => {
  const body = new Uint8Array(count * DROP_RECORD_BYTES);
  for (let i = 0; i < count; i += 1) {
    const index = first + i;
    const data = await readJoined(files, index * DROP_RECORD_PLAINTEXT_BYTES, session.recordDataBytes(index));
    body.set(await session.sealRecord(index, data), i * DROP_RECORD_BYTES);
  }
  return body;
};

/**
 * Upload every part not yet done. Resolves when all have arrived; rejects on
 * an error a retry won't fix, or when `signal` aborts. A retried part is
 * sealed again, with fresh IVs (crypto-spec §7.4).
 */
export const uploadParts = async (options: UploadOptions): Promise<void> => {
  const queue = Array.from({ length: options.parts }, (_, i) => i + 1).filter((part) => !options.done.has(part));
  // One part failing for good stops the others too.
  const stop = new AbortController();
  const signal = AbortSignal.any([options.signal, stop.signal]);
  // Whole parts already done count in full; parts in flight count what they've sent.
  const partBytes = (part: number) => partRecords(part, options.partSize, options.session.totalRecords).count * DROP_RECORD_BYTES;
  let doneBytes = [...options.done].reduce((sum, part) => sum + partBytes(part), 0);
  const inFlight = new Map<number, number>();
  const report = () => options.onBytes?.(doneBytes + [...inFlight.values()].reduce((a, b) => a + b, 0));
  report();
  const worker = async () => {
    for (let part = queue.shift(); part !== undefined; part = queue.shift()) {
      const { first, count } = partRecords(part, options.partSize, options.session.totalRecords);
      for (let attempt = 0; ; attempt += 1) {
        signal.throwIfAborted();
        const current = part;
        inFlight.set(current, 0);
        try {
          await options.put(current, await sealPart(options.files, options.session, first, count), signal, (sent) => {
            inFlight.set(current, sent);
            report();
          });
          options.onRetry?.(null);
          break;
        } catch (err) {
          inFlight.set(current, 0);
          report();
          if (signal.aborted || !isRetryable(err)) {
            stop.abort(err);
            throw err;
          }
          options.onRetry?.(retryDelay(attempt));
          await wait(retryDelay(attempt), signal);
        }
      }
      inFlight.delete(part);
      doneBytes += partBytes(part);
      options.done.add(part);
      report();
      options.onPart(part);
    }
  };
  await Promise.all(Array.from({ length: Math.min(options.concurrency ?? 3, Math.max(queue.length, 1)) }, worker));
};
